// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import Axios from 'axios';
import { injectable, unmanaged } from 'inversify';

import { ListRequestParams, ListRequestResponse } from '@shared/types/services';
import { Id } from '@shared/types/common';

type ModelConstructor<K> = new (...args: any[]) => K;

@injectable()
export default class BaseService<T extends InstanceType<any>, K extends InstanceType<any>> {
  static diToken = Symbol('base-service');
  protected urlPrefix: string;
  protected Model: ModelConstructor<K>;
  private domainArea: string;

  constructor(@unmanaged() { domainArea, Model }: { domainArea: string; Model: ModelConstructor<K> }) {
    this.urlPrefix = `/${domainArea}`;
    this.domainArea = domainArea;
    this.Model = Model;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getListQuery(params: ListRequestParams): string { // Note: virt func, will be implemented by derived class.
    return '';
  }

  getInfoFromListResponse(data: { [key: string]: ListRequestResponse<T> }) {
    const { [this.domainArea]: { items, pageInfo } } = data;

    return { items, pageInfo };
  }

  async getList(params: ListRequestParams) {
    try {
      const { data } = await Axios.get<{ [key: string]: ListRequestResponse<T> }>(
        this.urlPrefix,
        { params: { query: this.getListQuery(params) } }
      );

      const { items, pageInfo } = this.getInfoFromListResponse(data);

      return {
        pageInfo,
        items: items.map((dto) => new this.Model(dto)),
      };
    } 
    catch (err) {
      throw err;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  create(data: Partial<T>, ...args: Array<any>): Promise<void | any> {
    return Axios.post(this.urlPrefix, data);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async get(id: Id, ...args: Array<any>): Promise<K> {
    try {
      const { data } = await Axios.get<T>(`${this.urlPrefix}/${id}`);
      return new this.Model(data);
    } 
    catch (err) 
    {
      console.log("****** Exception in service base 'get'.", err);
      throw err;
    }
  }

  // Send a request to update the order to portal-api.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  edit(orderId: Id, data: Partial<T>, ...args: Array<any>): Promise<void> {
    return Axios.put(`${this.urlPrefix}/${orderId}`, data);
  }

  getURL(path: Id) {
    return `${this.urlPrefix}/${path}`;
  }

  delete(id: Id): Promise<void> {
    return Axios.delete(`${this.urlPrefix}/${id}`);
  }
}
